import type { SortDirection } from '../../claim-history/simple-table'
import type { UseQueryResult } from '@tanstack/react-query'

import { useQuery } from '@tanstack/react-query'

import http from '../../../lib/http'

interface UseDocumentQueryOptions {
  field: keyof Document
  dir: SortDirection
  size: number
  page: number
}

interface UseDocumentQueryProps {
  patientId?: string
  options: UseDocumentQueryOptions
}

export interface QueryResponse {
  data: Document[]
  total: number
}

export interface Document extends Record<string, unknown>{
  id: string
  file: {
    'hash'?: string
    'size'?: number
    'title': string
    'creation'?: string
    'language'?: string
    'contentType'?: string
  }
  date_upload?: string
  description?: string
  created?: string
  document_type?: string
  not_visible?: boolean
}

type QueryKey = ['behavioral-document', string | undefined, UseDocumentQueryOptions | undefined] | ['behavioral-document', string | undefined]

interface documentQueryKeyArg {
  patientId: string | undefined
  options?: UseDocumentQueryOptions
}

export function documentQueryKey ({ patientId, options }: documentQueryKeyArg): QueryKey {
  if (patientId && options) {
    return [ 'behavioral-document', patientId, options ]
  }

  return [ 'behavioral-document', patientId ]
}

export function useDocumentsQuery ({ patientId, options }: UseDocumentQueryProps): UseQueryResult<QueryResponse | null, Error> {
  return useQuery<QueryResponse | null, Error, QueryResponse | null, QueryKey>({
    queryKey: documentQueryKey({ patientId, options }),
    queryFn: async ({ queryKey }) => {
      const [ , patientId, options ] = queryKey
      if (!patientId) {
        return null
      }

      return await http.getRequest<QueryResponse>(`/behavioral/${patientId}/documents`, {
        field: options?.field ?? undefined,
        dir: options?.dir ?? undefined,
        page: options?.page ?? undefined,
        size: options?.size ?? undefined
      })
    },
    refetchOnWindowFocus: false
  })
}
